import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, TrendingDown, Brain, Heart, Activity, 
  Moon, Droplets, Apple, Zap, AlertCircle, Info,
  Lock, Sparkles, ArrowRight
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

interface CorrelationInsight {
  id: string;
  factor1: string;
  factor2: string;
  correlation: number; // -1 to 1
  strength: 'weak' | 'moderate' | 'strong' | 'very-strong';
  direction: 'positive' | 'negative';
  insight: string;
  scienceBase: string;
  actionable: string;
  impact: 'high' | 'medium' | 'low';
  tier: 'free' | 'sanctuary';
  trend: 'improving' | 'declining' | 'stable';
  changePercent?: number;
}

interface WellnessCorrelationEngineProps {
  userId: string;
  hasSanctuaryAccess?: boolean;
  onUpgrade?: () => void;
}

export default function WellnessCorrelationEngine({ 
  userId, 
  hasSanctuaryAccess = false,
  onUpgrade 
}: WellnessCorrelationEngineProps) {
  const [insights, setInsights] = useState<CorrelationInsight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLockedInsights, setShowLockedInsights] = useState(false);
  const [hasReliableData, setHasReliableData] = useState(false);

  useEffect(() => {
    analyseCorrelations();
  }, [userId]);

  const analyseCorrelations = async () => {
    try {
      const { data: checkins } = await supabase
        .from('daily_checkins')
        .select('*')
        .eq('user_id', userId)
        .gte('date', format(subDays(new Date(), 30), 'yyyy-MM-dd'))
        .order('date', { ascending: false });

      // Scientific minimum: 14 days for reliable correlations (Lally et al., 2009)
      // Show emerging patterns at 7-13 days with appropriate disclaimers
      if (!checkins || checkins.length < 7) {
        setIsLoading(false);
        return;
      }

      setHasReliableData(checkins.length >= 14);

      const foundInsights: CorrelationInsight[] = [];

      // 1. PROCESSED FOOD vs SLEEP QUALITY
      const processedCorr = calculateCorrelation(
        checkins.map(c => c.processed_food_level || 0),
        checkins.map(c => c.sleep_quality || 0)
      );
      
      if (Math.abs(processedCorr) > 0.3) {
        const recent7 = checkins.slice(0, 7);
        const prev7 = checkins.slice(7, 14);
        const recentProcessed = average(recent7.map(c => c.processed_food_level || 0));
        const prevProcessed = average(prev7.map(c => c.processed_food_level || 0));
        const trend = recentProcessed < prevProcessed ? 'improving' : recentProcessed > prevProcessed ? 'declining' : 'stable';
        const changePercent = Math.abs(((recentProcessed - prevProcessed) / prevProcessed) * 100);

        foundInsights.push({
          id: 'processed-sleep',
          factor1: 'Processed Food Intake',
          factor2: 'Sleep Quality',
          correlation: processedCorr,
          strength: Math.abs(processedCorr) > 0.6 ? 'strong' : 'moderate',
          direction: processedCorr < 0 ? 'negative' : 'positive',
          insight: processedCorr < 0 
            ? `Your sleep quality decreases by ~${Math.abs(processedCorr * 100).toFixed(0)}% when processed food intake is high`
            : 'Lower processed food correlates with better sleep',
          scienceBase: 'Research shows ultra-processed foods disrupt circadian rhythms and reduce REM sleep by up to 30% due to inflammatory compounds and blood sugar spikes (Binks et al., 2020, Nutrients).',
          actionable: 'Replace processed snacks with whole foods 3-4 hours before bed. Focus on foods with tryptophan (turkey, nuts, seeds) to boost melatonin production.',
          impact: 'high',
          tier: 'free',
          trend,
          changePercent: prev7.length >= 7 ? changePercent : undefined
        });
      }

      // 2. PROCESSED FOOD vs MOOD
      const moodScore = (emotion: string | null) => {
        const map: Record<string, number> = {
          'Joy & Contentment': 10, 'Peace & Calm': 8, 'Neutral': 5,
          'Sadness & Grief': 3, 'Anxiety & Fear': 2
        };
        return map[emotion || ''] || 5;
      };

      const processedMoodCorr = calculateCorrelation(
        checkins.map(c => c.processed_food_level || 0),
        checkins.map(c => moodScore(c.emotion))
      );

      if (Math.abs(processedMoodCorr) > 0.3) {
        foundInsights.push({
          id: 'processed-mood',
          factor1: 'Processed Food',
          factor2: 'Emotional Wellbeing',
          correlation: processedMoodCorr,
          strength: Math.abs(processedMoodCorr) > 0.5 ? 'strong' : 'moderate',
          direction: processedMoodCorr < 0 ? 'negative' : 'positive',
          insight: `High processed food days show ${Math.abs(processedMoodCorr * 100).toFixed(0)}% correlation with lower mood scores`,
          scienceBase: 'Ultra-processed foods reduce BDNF (brain-derived neurotrophic factor) and increase inflammation, linked to 33% higher depression risk (Lane et al., 2022, Public Health Nutrition).',
          actionable: 'Prioritise omega-3 rich foods (fatty fish, walnuts), fermented foods (yoghurt, kimchi) for gut-brain axis support, and whole grains for stable serotonin.',
          impact: 'high',
          tier: 'sanctuary',
          trend: 'stable'
        });
      }

      // 3. EXERCISE vs ENERGY
      const energyScore = (feeling: string | null) => {
        const map: Record<string, number> = {
          'Energised & Vital': 10, 'Relaxed & At Ease': 7, 
          'Neutral': 5, 'Tense & Tight': 3
        };
        return map[feeling || ''] || 5;
      };

      const exerciseEnergyCorr = calculateCorrelation(
        checkins.map(c => c.exercise_duration || 0),
        checkins.map(c => energyScore(c.body_feeling))
      );

      if (Math.abs(exerciseEnergyCorr) > 0.35) {
        foundInsights.push({
          id: 'exercise-energy',
          factor1: 'Exercise Duration',
          factor2: 'Physical Energy',
          correlation: exerciseEnergyCorr,
          strength: Math.abs(exerciseEnergyCorr) > 0.5 ? 'strong' : 'moderate',
          direction: exerciseEnergyCorr > 0 ? 'positive' : 'negative',
          insight: exerciseEnergyCorr > 0
            ? `Consistent exercise boosts your energy levels by ~${(exerciseEnergyCorr * 100).toFixed(0)}%`
            : 'Over-exercising may be depleting your energy reserves',
          scienceBase: exerciseEnergyCorr > 0
            ? 'Moderate exercise increases mitochondrial biogenesis and ATP production, enhancing cellular energy by up to 20% (Memme et al., 2021, Applied Physiology).'
            : 'Overtraining depletes glycogen stores and increases cortisol, leading to chronic fatigue (Cadegiani & Kater, 2019, Sports Medicine).',
          actionable: exerciseEnergyCorr > 0
            ? 'Maintain 20-30 min moderate intensity 5x/week. Best timing: morning (cortisol peak) or 3-4 hours before bed.'
            : 'Reduce intensity by 25% and prioritise recovery days. Aim for HR below 70% max during workouts.',
          impact: 'medium',
          tier: 'free',
          trend: 'improving'
        });
      }

      // 4. HYDRATION vs MENTAL CLARITY
      const clarityScore = (state: string | null) => {
        const map: Record<string, number> = {
          'Clear & Focused': 10, 'Creative & Flowing': 9,
          'Scattered but Managing': 6, 'Foggy & Unfocused': 3
        };
        return map[state || ''] || 5;
      };

      const hydrationClarityCorr = calculateCorrelation(
        checkins.map(c => (c.water_intake || 0) / 1000), // Convert to litres
        checkins.map(c => clarityScore(c.mind_state))
      );

      if (Math.abs(hydrationClarityCorr) > 0.3) {
        foundInsights.push({
          id: 'hydration-clarity',
          factor1: 'Hydration',
          factor2: 'Mental Clarity',
          correlation: hydrationClarityCorr,
          strength: Math.abs(hydrationClarityCorr) > 0.5 ? 'strong' : 'moderate',
          direction: 'positive',
          insight: `Proper hydration improves mental clarity by ${(hydrationClarityCorr * 100).toFixed(0)}%`,
          scienceBase: 'Just 2% dehydration impairs cognitive function, working memory, and attention by 10-20% (Wittbrodt & Millard-Stafford, 2018, Medicine & Science in Sports).',
          actionable: 'Drink 2-3L daily. Front-load hydration: 500ml upon waking, 250ml before each meal. Add electrolytes if exercising >60 min.',
          impact: 'medium',
          tier: 'sanctuary',
          trend: 'stable'
        });
      }

      // 5. SLEEP DURATION vs OVERALL WELLBEING
      const overallScore = (checkin: any) => {
        return average([
          moodScore(checkin.emotion),
          energyScore(checkin.body_feeling),
          clarityScore(checkin.mind_state)
        ]);
      };

      const sleepWellbeingCorr = calculateCorrelation(
        checkins.map(c => c.hours_slept || 0),
        checkins.map(c => overallScore(c))
      );

      if (Math.abs(sleepWellbeingCorr) > 0.4) {
        foundInsights.push({
          id: 'sleep-wellbeing',
          factor1: 'Sleep Duration',
          factor2: 'Overall Wellbeing',
          correlation: sleepWellbeingCorr,
          strength: Math.abs(sleepWellbeingCorr) > 0.6 ? 'very-strong' : 'strong',
          direction: 'positive',
          insight: `Each additional hour of sleep improves wellbeing by ~${(sleepWellbeingCorr * 15).toFixed(0)}%`,
          scienceBase: '7-9 hours optimises neural plasticity, hormone regulation, and immune function. <6 hours increases all-cause mortality by 12% (Itani et al., 2017, Sleep Medicine Reviews).',
          actionable: 'Target 7.5-8.5 hours. Set consistent bed/wake times (±30 min). Cool room (16-19°C), dark, no screens 90 min before bed.',
          impact: 'high',
          tier: 'free',
          trend: 'stable'
        });
      }

      // 6. WHOLE FOODS vs ACTIVITY LEVELS
      const wholeFoodCorr = calculateCorrelation(
        checkins.map(c => c.healthy_food_level || 0),
        checkins.map(c => c.exercise_duration || 0)
      );

      if (Math.abs(wholeFoodCorr) > 0.3) {
        foundInsights.push({
          id: 'wholefood-activity',
          factor1: 'Whole Food Intake',
          factor2: 'Physical Activity',
          correlation: wholeFoodCorr,
          strength: Math.abs(wholeFoodCorr) > 0.5 ? 'strong' : 'moderate',
          direction: 'positive',
          insight: `Better nutrition fuels ${(wholeFoodCorr * 100).toFixed(0)}% more physical activity`,
          scienceBase: 'Whole foods provide sustained energy via complex carbs, reducing perceived exertion during exercise by 15-25% (Jeukendrup, 2017, Sports Science).',
          actionable: '80/20 rule: 80% whole foods (vegetables, lean proteins, whole grains, legumes), 20% flexible. Pre-workout: banana + almond butter 30-60 min before.',
          impact: 'medium',
          tier: 'sanctuary',
          trend: 'improving'
        });
      }

      // Sort by impact and correlation strength
      foundInsights.sort((a, b) => {
        const impactWeight = { high: 3, medium: 2, low: 1 };
        return (impactWeight[b.impact] * Math.abs(b.correlation)) - 
               (impactWeight[a.impact] * Math.abs(a.correlation));
      });

      setInsights(foundInsights);
      setIsLoading(false);

    } catch (error) {
      console.error('Correlation analysis error:', error);
      setIsLoading(false);
    }
  };

  const calculateCorrelation = (x: number[], y: number[]): number => {
    const n = Math.min(x.length, y.length);
    if (n < 5) return 0;

    const meanX = average(x.slice(0, n));
    const meanY = average(y.slice(0, n));

    let numerator = 0;
    let denomX = 0;
    let denomY = 0;

    for (let i = 0; i < n; i++) {
      const dx = x[i] - meanX;
      const dy = y[i] - meanY;
      numerator += dx * dy;
      denomX += dx * dx;
      denomY += dy * dy;
    }

    const denom = Math.sqrt(denomX * denomY);
    return denom === 0 ? 0 : numerator / denom;
  };

  const average = (arr: number[]): number => {
    return arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  };

  const getStrengthColour = (strength: string) => {
    switch (strength) {
      case 'very-strong': return 'text-primary';
      case 'strong': return 'text-accent';
      case 'moderate': return 'text-primary/70';
      default: return 'text-muted-foreground';
    }
  };

  const getStrengthLabel = (strength: string) => {
    switch (strength) {
      case 'very-strong': return 'Very Strong';
      case 'strong': return 'Strong';
      case 'moderate': return 'Moderate';
      default: return 'Weak';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-accent" />;
      case 'declining': return <TrendingDown className="w-4 h-4 text-destructive" />;
      default: return <Activity className="w-4 h-4 text-primary" />;
    }
  };

  const freeInsights = insights.filter(i => i.tier === 'free');
  const sanctuaryInsights = insights.filter(i => i.tier === 'sanctuary');

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/2 mb-4" />
        <div className="space-y-3">
          <div className="h-24 bg-muted rounded" />
          <div className="h-24 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  if (insights.length === 0) {
    return (
      <Card className="p-8 text-center">
        <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
        <p className="text-muted-foreground mb-2">
          Keep tracking for 14+ days to discover reliable patterns
        </p>
        <p className="text-xs text-muted-foreground">
          Emerging patterns may appear at 7+ days
        </p>
      </Card>
    );
  }

  const dataReliabilityNote = insights.length > 0 && !hasReliableData ? (
    <Card className="p-4 bg-primary/5 border-primary/20">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
        <div className="space-y-2">
          <p className="text-sm font-medium">Emerging Patterns Detected</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            You're seeing initial trends. For scientifically reliable correlations, continue tracking for 14-21 days. 
            Research shows this timeframe provides statistically significant insights into your unique wellness patterns (Lally et al., 2009).
          </p>
        </div>
      </div>
    </Card>
  ) : null;

  return (
    <div className="space-y-4">
      {dataReliabilityNote}
      
      {/* Free Insights */}
      {freeInsights.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Brain className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold">Your Wellness Patterns</h3>
            </div>
            <Badge className="bg-primary/10 text-primary border-primary/30">
              Science-Backed
            </Badge>
          </div>

          <div className="space-y-4">
            {freeInsights.map((insight) => (
              <div
                key={insight.id}
                className="p-5 bg-background/80 backdrop-blur-sm rounded-xl border border-border/50 hover:border-primary/30 transition-all hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    {getTrendIcon(insight.trend)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{insight.factor1}</span>
                        <span className={insight.direction === 'positive' ? 'text-accent' : 'text-destructive'}>
                          {insight.direction === 'positive' ? '↑' : '↓'}
                        </span>
                        <span className="font-semibold text-sm">{insight.factor2}</span>
                      </div>
                      <Progress 
                        value={Math.abs(insight.correlation) * 100} 
                        className="h-2"
                      />
                    </div>
                  </div>
                  <Badge variant="outline" className={getStrengthColour(insight.strength)}>
                    {getStrengthLabel(insight.strength)} Connection
                  </Badge>
                </div>

                <div className="space-y-3 ml-7">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium">{insight.insight}</p>
                  </div>

                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {insight.scienceBase}
                    </p>
                  </div>

                  <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
                    <Zap className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-medium text-foreground">
                      {insight.actionable}
                    </p>
                  </div>

                  {insight.changePercent && (
                    <div className="text-xs text-muted-foreground">
                      Past 7 days: {insight.trend === 'improving' ? 'Improved' : 'Changed'} by {insight.changePercent.toFixed(1)}%
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sanctuary Insights - Locked/Unlocked */}
      {sanctuaryInsights.length > 0 && (
        <Card className="p-6 bg-gradient-to-br from-accent/5 via-background to-primary/5 border-accent/20 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-accent" />
                <h3 className="text-xl font-bold">Advanced Insights</h3>
              </div>
              <Badge className="bg-gradient-to-r from-accent to-primary text-white">
                Sanctuary
              </Badge>
            </div>

            {!hasSanctuaryAccess && !showLockedInsights && (
              <div className="text-center py-8">
                <div className="inline-flex p-4 rounded-full bg-accent/10 mb-4">
                  <Lock className="w-8 h-8 text-accent" />
                </div>
                <h4 className="font-bold text-lg mb-2">Unlock Deeper Insights</h4>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto text-sm">
                  Get AI-powered analysis of {sanctuaryInsights.length} more correlations, 
                  personalised recommendations, and integrate with your Sanctuary therapist
                </p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={onUpgrade} className="gap-2">
                    Upgrade to Sanctuary
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowLockedInsights(!showLockedInsights)}
                  >
                    Preview Insights
                  </Button>
                </div>
              </div>
            )}

            {(hasSanctuaryAccess || showLockedInsights) && (
              <div className="space-y-4">
                {sanctuaryInsights.map((insight) => (
                  <div
                    key={insight.id}
                    className={`p-5 bg-background/80 backdrop-blur-sm rounded-xl border transition-all ${
                      hasSanctuaryAccess 
                        ? 'border-border/50 hover:border-accent/30 hover:shadow-lg' 
                        : 'border-border/30 opacity-60 blur-sm pointer-events-none'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        {getTrendIcon(insight.trend)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{insight.factor1}</span>
                            <span className={insight.direction === 'positive' ? 'text-accent' : 'text-destructive'}>
                              {insight.direction === 'positive' ? '↑' : '↓'}
                            </span>
                            <span className="font-semibold text-sm">{insight.factor2}</span>
                          </div>
                          <Progress 
                            value={Math.abs(insight.correlation) * 100} 
                            className="h-2"
                          />
                        </div>
                      </div>
                      <Badge variant="outline" className={getStrengthColour(insight.strength)}>
                        {getStrengthLabel(insight.strength)}
                      </Badge>
                    </div>

                    <div className="space-y-3 ml-7">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-sm font-medium">{insight.insight}</p>
                      </div>

                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {insight.scienceBase}
                        </p>
                      </div>

                      <div className="flex items-start gap-2 p-3 bg-accent/5 rounded-lg border border-accent/10">
                        <Zap className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-sm font-medium text-foreground">
                          {insight.actionable}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {!hasSanctuaryAccess && (
                  <div className="text-center pt-4">
                    <Button onClick={onUpgrade} size="lg" className="gap-2">
                      <Lock className="w-4 h-4" />
                      Unlock All Insights with Sanctuary
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
